import { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import LotusLoader from "../components/LotusLoader";
import BrandHeader from "../components/BrandHeader";
import Particles from "../components/Particles";
import PrescriptionForm from "./PrescriptionForm";
import { resumeApi } from "../lib/api";

/**
 * Recovery email deep-link landing page.
 * URL: /resume?k=<token>&src=email_1|email_2|email_3
 *
 * Flow:
 *  - Lookup token via GET /api/resume/{token}
 *  - paid          → "already complete" message
 *  - expired       → "expired, start fresh"
 *  - reached_payment / completed_quiz → /reveal/:orderId  (one-click payment resume)
 *  - else          → render the wizard pre-filled, jumped to the next incomplete step
 */
export default function Resume() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const token = params.get("k");
  const src = params.get("src") || "";

  const [data, setData] = useState(null);
  // Derive initial error/loading from the token presence so the effect only handles
  // the async fetch path (avoids synchronous setState inside useEffect).
  const [error, setError] = useState(token ? null : "missing_token");
  const [loading, setLoading] = useState(!!token);

  useEffect(() => {
    if (!token) return;
    resumeApi.lookup(token, src)
      .then((d) => setData(d))
      .catch((e) => {
        const msg = e?.response?.data?.detail || "invalid_token";
        setError(msg);
      })
      .finally(() => setLoading(false));
  }, [token, src]);

  // Auto-redirect to reveal for users who already completed quiz / reached payment
  useEffect(() => {
    if (!data) return;
    if (data.state === "ok" && data.resume_step >= 3) {
      navigate(`/reveal/${data.orderId}`, { replace: true });
    }
  }, [data, navigate]);

  if (loading) return <LotusLoader context="general" />;

  if (error || !data) {
    return (
      <ResumeMessage
        title="This link has expired"
        body="Please begin a new prescription."
        ctaText="Begin a new reading"
        ctaTo="/prescription"
        testid="resume-invalid"
      />
    );
  }

  if (data.state === "paid") {
    return (
      <ResumeMessage
        title="Your prescription is already complete."
        body="Check your email for your reading details."
        ctaText="Back to home"
        ctaTo="/"
        testid="resume-paid"
      />
    );
  }

  if (data.state === "expired") {
    return (
      <ResumeMessage
        title="Your previous prescription has expired"
        body="Welcome back — let's begin fresh."
        ctaText="Start a new reading"
        ctaTo="/prescription"
        secondaryText="Continue with previous data"
        secondaryTo={`/prescription?continue=${data.orderId}`}
        testid="resume-expired"
      />
    );
  }

  // Resume into the wizard at the right step
  return (
    <PrescriptionForm
      isResume
      resumeData={data}
      initialStep={Math.max(1, Math.min(2, data.resume_step || 1))}
    />
  );
}


function ResumeMessage({ title, body, ctaText, ctaTo, secondaryText, secondaryTo, testid }) {
  return (
    <div className="min-h-screen pf-wizard-root flex items-center justify-center" data-testid={testid}>
      <Particles variant="violet" />
      <BrandHeader />
      <div className="relative max-w-md w-full mx-auto px-5 text-center py-20">
        <h1 className="font-display text-3xl text-white">{title}</h1>
        <p className="font-serif italic mt-4" style={{ color: "#FAF7F2" }}>{body}</p>
        <Link to={ctaTo} className="pf-cta inline-flex mt-8" data-testid="resume-cta">{ctaText} <span aria-hidden>&rarr;</span></Link>
        {secondaryText && (
          <div className="mt-3">
            <Link to={secondaryTo} className="text-sm" style={{ color: "#D4A855" }}>{secondaryText}</Link>
          </div>
        )}
      </div>
    </div>
  );
}
